import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { Link, useSearchParams } from "react-router-dom";
import { gsap } from "gsap";
import NeuralField from "../components/landing/NeuralField";
import { Container, Eyebrow, Lead, Arrow } from "../components/landing/ui";
import { colors, fonts, media, copperGradient, prefersReducedMotion } from "../styles/theme";
import { verticals } from "./verticalContent";
import { award, platforms, contact } from "./landingContent";

// Where submissions go, in order of preference:
// 1. googleForm: posts straight into a Google Form (responses land in Forms / Sheets).
//    Fill in `formId` and each field's `entry.<number>` id from the form's prefilled link.
// 2. endpoint: a JSON form service (Formspree, HubSpot, serverless function).
// 3. email: fallback that opens a prefilled email so no enquiry is lost.
export const registerConfig = {
  googleForm: {
    // "vimbal – Register interest" in Google Forms
    formId: "1FAIpQLSf7hapKAqkoXeOnMjLDH0Aud98ESEhctgda8Ke0R_NKKKJGWg",
    fields: {
      firstName: "entry.1612328977",
      lastName: "entry.322200353",
      email: "entry.1806846885",
      organization: "entry.942276662",
      role: "entry.342955116",
      country: "entry.740408321",
      interest: "entry.1048008277",
      intent: "entry.857029425",
      message: "entry.297752983",
    },
  },
  endpoint: "",
  email: contact.email,
};

const googleFormReady = () => {
  const g = registerConfig.googleForm;
  return Boolean(g && g.formId && g.fields.firstName && g.fields.email);
};

// Google Forms doesn't allow reading the response cross-origin, so this is fire-and-forget:
// the request is sent in "no-cors" mode and treated as success unless the network fails.
const submitToGoogleForm = async (data) => {
  const { formId, fields } = registerConfig.googleForm;
  const body = new URLSearchParams();
  Object.entries(fields).forEach(([key, entry]) => {
    if (entry && data[key] !== undefined && data[key] !== "") body.append(entry, data[key]);
  });
  await fetch(`https://docs.google.com/forms/d/e/${formId}/formResponse`, {
    method: "POST",
    mode: "no-cors",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
};

const INTERESTS = [...verticals.map((v) => ({ value: v.slug, label: v.nav })), { value: "other", label: "Something else" }];

const INTENTS = [
  "Try vimbal myself",
  "Bring vimbal to my organization",
  "Run a site program",
  "Partner with vimbal",
];

const NEXT_STEPS = [
  { title: "We read every inquiry", body: "A real person from the vimbal team reviews what you've shared." },
  { title: "We reach out", body: "Expect a reply by email to find the best way forward for you." },
  { title: "Experience vimbal", body: "From a demonstration to a site program, we'll get you into the environment." },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ---------- layout ---------- */

const Page = styled.section`
  position: relative;
  padding: 140px 0 120px;
  background:
    radial-gradient(50% 50% at 100% 0%, rgba(216, 168, 137, 0.3), transparent 70%),
    radial-gradient(40% 50% at 0% 50%, rgba(163, 177, 143, 0.25), transparent 70%);
`;

const Grid = styled.div`
  display: grid;
  gap: 48px;
  align-items: start;

  @media ${media.large} {
    grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
    gap: 72px;
  }
`;

const Aside = styled.div`
  @media ${media.large} {
    position: sticky;
    top: 110px;
  }
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(48px, 6vw, 92px);
  line-height: 0.96;
  letter-spacing: -0.04em;
  color: ${colors.ink};
  margin: 20px 0 24px;

  em {
    display: block;
    font-style: normal;
    color: ${colors.copper};
  }
`;

const Steps = styled.ol`
  list-style: none;
  counter-reset: step;
  display: grid;
  gap: 4px;
  margin: 36px 0 28px;

  li {
    counter-increment: step;
    display: grid;
    grid-template-columns: 44px 1fr;
    gap: 14px;
    padding: 16px 0;
    border-top: 1px solid ${colors.stone};
  }
  li::before {
    content: counter(step, decimal-leading-zero);
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${colors.forest};
    color: ${colors.linen};
    font-family: ${fonts.mono};
    font-size: 12px;
  }
  b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 21px;
    letter-spacing: -0.015em;
    color: ${colors.ink};
  }
  span {
    font-family: ${fonts.body};
    font-size: 15px;
    line-height: 1.5;
    color: ${colors.bark};
  }
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  span,
  a {
    padding: 8px 14px;
    border-radius: 999px;
    border: 1px solid ${colors.stone};
    background: ${colors.linen};
    font-family: ${fonts.body};
    font-size: 14px;
    font-weight: 600;
    color: ${colors.ink};
  }
  a {
    color: ${colors.copper};
  }
`;

const Talk = styled.p`
  margin-top: 24px;
  font-family: ${fonts.body};
  font-size: 16px;
  line-height: 1.6;
  color: ${colors.bark};

  a {
    color: ${colors.copper};
    font-weight: 600;
    white-space: nowrap;
  }
  a:hover {
    text-decoration: underline;
  }
`;

/* ---------- form ---------- */

const Card = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 32px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};
  box-shadow: 0 50px 100px -60px rgba(42, 35, 30, 0.5);
  padding: 28px 22px;

  @media ${media.small} {
    padding: 44px;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 22px;
`;

const Row = styled.div`
  display: grid;
  gap: 22px;

  @media ${media.small} {
    grid-template-columns: 1fr 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 8px;
`;

const LabelText = styled.label`
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 600;
  color: ${colors.ink};

  small {
    font-weight: 400;
    color: ${colors.pebble};
    margin-left: 6px;
  }
`;

const inputStyles = css`
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid ${(p) => (p.$invalid ? colors.terracotta : colors.stone)};
  background: ${colors.limestone};
  font-family: ${fonts.body};
  font-size: 16px;
  color: ${colors.ink};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  -webkit-appearance: none;
  appearance: none;

  &::placeholder {
    color: ${colors.pebble};
  }
  &:hover {
    border-color: ${(p) => (p.$invalid ? colors.terracotta : colors.pebble)};
  }
  &:focus {
    outline: none;
    background: ${colors.linen};
    border-color: ${colors.moss};
    box-shadow: 0 0 0 4px rgba(79, 100, 69, 0.15);
  }
`;

const Input = styled.input`
  ${inputStyles}
`;

const Textarea = styled.textarea`
  ${inputStyles}
  min-height: 130px;
  resize: vertical;
  line-height: 1.5;
`;

const FieldError = styled.p`
  font-family: ${fonts.body};
  font-size: 14px;
  color: ${colors.terracotta};
`;

const Fieldset = styled.fieldset`
  border: 0;
  display: grid;
  gap: 10px;

  legend {
    padding: 0;
    margin-bottom: 10px;
    font-family: ${fonts.body};
    font-size: 15px;
    font-weight: 600;
    color: ${colors.ink};
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.label`
  position: relative;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }
  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 999px;
    border: 1px solid ${colors.stone};
    background: ${colors.limestone};
    font-family: ${fonts.body};
    font-size: 15px;
    font-weight: 500;
    color: ${colors.bark};
    transition: all 0.2s ease;
  }
  span::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => p.$dot || colors.stone};
  }
  &:hover span {
    border-color: ${colors.pebble};
  }
  input:checked + span {
    background: ${colors.ink};
    border-color: ${colors.ink};
    color: ${colors.linen};
  }
  input:focus-visible + span {
    box-shadow: 0 0 0 4px rgba(143, 83, 41, 0.25);
  }
`;

const Consent = styled.label`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: 15px;
  line-height: 1.5;
  color: ${colors.bark};

  input {
    flex: none;
    width: 20px;
    height: 20px;
    margin-top: 2px;
    accent-color: ${colors.moss};
    -webkit-appearance: auto;
    appearance: auto;
    cursor: pointer;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Submit = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 56px;
  padding: 0 30px;
  border-radius: 999px;
  font-family: ${fonts.body};
  font-size: 17px;
  font-weight: 600;
  color: ${colors.linen};
  background: ${copperGradient};
  box-shadow: 0 12px 30px -12px rgba(143, 83, 41, 0.75);
  cursor: pointer;
  transition: transform 0.3s ease, opacity 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
  &[aria-busy="true"] {
    opacity: 0.75;
    cursor: progress;
  }
  i {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid rgba(250, 247, 241, 0.4);
    border-top-color: ${colors.linen};
    animation: ${spin} 0.8s linear infinite;
  }
`;

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  p {
    font-family: ${fonts.body};
    font-size: 13px;
    color: ${colors.pebble};
    max-width: 320px;
  }
`;

const FormError = styled.p`
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(154, 78, 46, 0.1);
  font-family: ${fonts.body};
  font-size: 15px;
  color: ${colors.terracotta};
`;

/* ---------- success ---------- */

const draw = keyframes`
  to { stroke-dashoffset: 0; }
`;

const Success = styled.div`
  position: relative;
  min-height: 560px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin: -28px -22px;
  padding: 40px 28px;
  background: ${colors.forest};
  color: ${colors.linen};

  @media ${media.small} {
    margin: -44px;
    padding: 56px;
  }

  canvas {
    opacity: 0.6;
  }
  > *:not(canvas) {
    position: relative;
  }
  svg path {
    stroke-dasharray: 60;
    stroke-dashoffset: 60;
    animation: ${draw} 0.8s 0.2s ease-out forwards;
  }
  h2 {
    margin: 24px 0 14px;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(40px, 5vw, 64px);
    line-height: 1;
    letter-spacing: -0.035em;
  }
  h2 em {
    font-style: normal;
    color: ${colors.copperLight};
  }
  p {
    font-family: ${fonts.body};
    font-size: 18px;
    line-height: 1.6;
    color: rgba(250, 247, 241, 0.8);
    max-width: 460px;
  }
`;

const SuccessLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border-radius: 999px;
    border: 1px solid rgba(250, 247, 241, 0.3);
    font-family: ${fonts.body};
    font-size: 15px;
    font-weight: 600;
    color: ${colors.linen};
  }
  a:hover {
    border-color: ${colors.linen};
  }
`;

/* ---------- component ---------- */

const EMPTY = {
  firstName: "",
  lastName: "",
  email: "",
  organization: "",
  role: "",
  country: "",
  interest: "",
  intent: "",
  message: "",
  consent: false,
};

const validate = (v) => {
  const e = {};
  if (!v.firstName.trim()) e.firstName = "Enter your first name.";
  if (!v.lastName.trim()) e.lastName = "Enter your last name.";
  if (!v.email.trim()) e.email = "Enter your email address.";
  else if (!EMAIL_RE.test(v.email.trim())) e.email = "Enter a valid email, like name@company.com.";
  if (!v.interest) e.interest = "Choose the area you're most interested in.";
  if (!v.intent) e.intent = "Choose what you'd like to do.";
  if (!v.consent) e.consent = "Please agree so we can reply to you.";
  return e;
};

const Register = () => {
  const scope = useRef(null);
  const formRef = useRef(null);
  const [params] = useSearchParams();
  const initialInterest = INTERESTS.some((i) => i.value === params.get("interest")) ? params.get("interest") : "";
  const [values, setValues] = useState({ ...EMPTY, interest: initialInterest });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  useEffect(() => {
    document.title = "vimbal · Register interest";
  }, []);

  useEffect(() => {
    if (initialInterest) setValues((v) => ({ ...v, interest: initialInterest }));
  }, [initialInterest]);

  useLayoutEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      gsap.from("[data-r-in]", { y: 30, opacity: 0, duration: 1, stagger: 0.08, ease: "power3.out" });
    }, scope);
    return () => ctx.revert();
  }, []);

  const set = (name) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: undefined }));
  };

  const interestLabel = (INTERESTS.find((i) => i.value === values.interest) || {}).label || "";

  const onSubmit = async (e) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) {
      const first = formRef.current.querySelector(`[name="${Object.keys(found)[0]}"]`);
      if (first) first.focus();
      return;
    }
    setStatus("sending");
    const payload = { ...values, interest: interestLabel, submittedAt: new Date().toISOString(), page: window.location.href };
    try {
      if (googleFormReady()) {
        await submitToGoogleForm({ ...values, interest: interestLabel });
      } else if (registerConfig.endpoint) {
        const res = await fetch(registerConfig.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
      } else {
        const lines = [
          `Name: ${values.firstName} ${values.lastName}`,
          `Email: ${values.email}`,
          `Organization: ${values.organization || "-"}`,
          `Role: ${values.role || "-"}`,
          `Country: ${values.country || "-"}`,
          `Area of interest: ${interestLabel}`,
          `I'd like to: ${values.intent}`,
          "",
          values.message || "",
        ];
        const subject = `Register interest: ${interestLabel} (${values.firstName} ${values.lastName})`;
        window.location.href = `mailto:${registerConfig.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
      }
      setStatus("sent");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
    }
  };

  const invalid = (name) => Boolean(errors[name]);

  return (
    <Page ref={scope} aria-labelledby="r-title">
      <Container>
        <Grid>
          <Aside>
            <div data-r-in>
              <Eyebrow $color={colors.copper}>Register interest</Eyebrow>
            </div>
            <Title id="r-title" data-r-in>
              Let's get you <em>into flow.</em>
            </Title>
            <Lead data-r-in>
              Tell us a little about you and what you'd like to do with vimbal, whether that's trying it yourself, bringing it to your organization, or running a site program.
            </Lead>
            <Steps data-r-in>
              {NEXT_STEPS.map((s) => (
                <li key={s.title}>
                  <div>
                    <b>{s.title}</b>
                    <span>{s.body}</span>
                  </div>
                </li>
              ))}
            </Steps>
            <Badges data-r-in>
              {platforms.devices.map((d) => (
                <span key={d}>{d}</span>
              ))}
              <a href={award.href} target="_blank" rel="noopener noreferrer">
                ★ {award.title} · {award.region}
              </a>
            </Badges>
            <Talk data-r-in>
              Prefer to talk? <a href={`mailto:${contact.email}`}>{contact.email}</a> ·{" "}
              <a href={contact.phoneHref}>{contact.phone}</a> ({contact.phoneNote})
            </Talk>
          </Aside>

          <Card data-r-in>
            {status === "sent" ? (
              <Success role="status">
                <NeuralField variant="dark" calm={1} lines={9} />
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                  <circle cx="32" cy="32" r="30" stroke={colors.sage} strokeWidth="2" />
                  <path d="M20 33l8 8 16-18" stroke={colors.copperLight} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <h2>
                  Thank you, <em>{values.firstName}.</em>
                </h2>
                <p>
                  {googleFormReady() || registerConfig.endpoint
                    ? "Your interest is registered. We'll be in touch soon."
                    : "Your email app should open with your details filled in. Just press send and we'll be in touch soon."}
                </p>
                <SuccessLinks>
                  <Link to="/">
                    Back to overview <Arrow />
                  </Link>
                  {values.interest && values.interest !== "other" && (
                    <Link to={`/${values.interest}`}>
                      More on {interestLabel.toLowerCase()} <Arrow />
                    </Link>
                  )}
                </SuccessLinks>
              </Success>
            ) : (
              <Form ref={formRef} onSubmit={onSubmit} noValidate aria-describedby="r-required">
                <Row>
                  <Field>
                    <LabelText htmlFor="firstName">First name</LabelText>
                    <Input id="firstName" name="firstName" autoComplete="given-name" value={values.firstName} onChange={set("firstName")} $invalid={invalid("firstName")} aria-invalid={invalid("firstName")} aria-describedby="firstName-err" />
                    {errors.firstName && <FieldError id="firstName-err">{errors.firstName}</FieldError>}
                  </Field>
                  <Field>
                    <LabelText htmlFor="lastName">Last name</LabelText>
                    <Input id="lastName" name="lastName" autoComplete="family-name" value={values.lastName} onChange={set("lastName")} $invalid={invalid("lastName")} aria-invalid={invalid("lastName")} aria-describedby="lastName-err" />
                    {errors.lastName && <FieldError id="lastName-err">{errors.lastName}</FieldError>}
                  </Field>
                </Row>

                <Field>
                  <LabelText htmlFor="email">Email</LabelText>
                  <Input id="email" name="email" type="email" autoComplete="email" placeholder="name@company.com" value={values.email} onChange={set("email")} $invalid={invalid("email")} aria-invalid={invalid("email")} aria-describedby="email-err" />
                  {errors.email && <FieldError id="email-err">{errors.email}</FieldError>}
                </Field>

                <Row>
                  <Field>
                    <LabelText htmlFor="organization">
                      Organization <small>optional</small>
                    </LabelText>
                    <Input id="organization" name="organization" autoComplete="organization" value={values.organization} onChange={set("organization")} />
                  </Field>
                  <Field>
                    <LabelText htmlFor="role">
                      Role <small>optional</small>
                    </LabelText>
                    <Input id="role" name="role" autoComplete="organization-title" value={values.role} onChange={set("role")} />
                  </Field>
                </Row>

                <Field>
                  <LabelText htmlFor="country">
                    Country <small>optional</small>
                  </LabelText>
                  <Input id="country" name="country" autoComplete="country-name" value={values.country} onChange={set("country")} />
                </Field>

                <Fieldset aria-describedby="interest-err">
                  <legend>Area of interest</legend>
                  <Chips>
                    {INTERESTS.map((i) => {
                      const v = verticals.find((x) => x.slug === i.value);
                      return (
                        <Chip key={i.value} $dot={v ? (v.dark ? colors.forest : v.accent) : colors.pebble}>
                          <input type="radio" name="interest" value={i.value} checked={values.interest === i.value} onChange={set("interest")} />
                          <span>{i.label}</span>
                        </Chip>
                      );
                    })}
                  </Chips>
                  {errors.interest && <FieldError id="interest-err">{errors.interest}</FieldError>}
                </Fieldset>

                <Fieldset aria-describedby="intent-err">
                  <legend>I'd like to</legend>
                  <Chips>
                    {INTENTS.map((i) => (
                      <Chip key={i} $dot={colors.copperGlow}>
                        <input type="radio" name="intent" value={i} checked={values.intent === i} onChange={set("intent")} />
                        <span>{i}</span>
                      </Chip>
                    ))}
                  </Chips>
                  {errors.intent && <FieldError id="intent-err">{errors.intent}</FieldError>}
                </Fieldset>

                <Field>
                  <LabelText htmlFor="message">
                    Anything else we should know? <small>optional</small>
                  </LabelText>
                  <Textarea id="message" name="message" placeholder="Team size, timing, what you'd like to achieve" value={values.message} onChange={set("message")} />
                </Field>

                <div>
                  <Consent>
                    <input type="checkbox" name="consent" checked={values.consent} onChange={set("consent")} aria-invalid={invalid("consent")} aria-describedby="consent-err" />
                    <span>I agree to vimbal contacting me about my inquiry.</span>
                  </Consent>
                  {errors.consent && <FieldError id="consent-err" style={{ marginTop: 8 }}>{errors.consent}</FieldError>}
                </div>

                {status === "error" && (
                  <FormError role="alert">
                    Something went wrong sending your details. Please try again, or email {registerConfig.email}.
                  </FormError>
                )}

                <Foot>
                  <p id="r-required">All fields are required unless marked optional.</p>
                  <Submit type="submit" aria-busy={status === "sending"}>
                    {status === "sending" ? (
                      <>
                        <i aria-hidden="true" /> Sending
                      </>
                    ) : (
                      <>
                        Register interest <Arrow />
                      </>
                    )}
                  </Submit>
                </Foot>
              </Form>
            )}
          </Card>
        </Grid>
      </Container>
    </Page>
  );
};

export default Register;
